import React, { useState, useEffect, useRef } from 'react';
import { X, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { contemplateFrameworkData } from '../../data/mock';

const PrinciplesFloatingButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedPrinciple, setExpandedPrinciple] = useState(null);
  const panelRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // Check if click is not on the button itself
        const button = document.getElementById('principles-floating-button');
        if (button && !button.contains(event.target)) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const togglePrinciple = (index) => {
    setExpandedPrinciple(expandedPrinciple === index ? null : index);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        id="principles-floating-button"
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 rounded-full bg-[#202020] text-[#efede6] shadow-2xl flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 transition-all duration-300 hover:scale-105 hover:shadow-3xl focus:outline-none focus:ring-4 focus:ring-[#202020]/30 ${
          isOpen ? '' : ''
        }`}
        aria-label="View Contemplate Framework Principles"
        style={{ animation: isOpen ? 'none' : 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
      >
        {isOpen ? (
          <X className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
        ) : (
          <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
        )}
        {!isOpen && (
          <>
            <span className="text-sm sm:text-base font-medium whitespace-nowrap">
              The Contemplate Framework
            </span>
            <span className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-[#efede6] rounded-full border-2 border-[#202020] animate-ping" />
          </>
        )}
      </button>

      {/* Principles Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[500px] max-w-2xl max-h-[80vh] bg-white rounded-2xl shadow-2xl border-2 border-[#202020]/10 overflow-hidden flex flex-col"
          style={{
            animation: 'slideInUp 0.3s ease-out'
          }}
        >
          {/* Header */}
          <div className="bg-[#202020] p-6 text-[#efede6] flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-normal tracking-tight mb-2">
                {contemplateFrameworkData.title}
              </h3>
              <p className="text-sm text-[#efede6]/70">
                {contemplateFrameworkData.subtitle}
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-[#202020]/80 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Description */}
            <div className="mb-8">
              <p className="text-[#202020]/70 leading-relaxed">
                {contemplateFrameworkData.description}
              </p>
            </div>

            {/* Principles List */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-[#202020] mb-4">
                Principles You&apos;ll Learn:
              </h4>
              {contemplateFrameworkData.principles.map((principle, index) => (
                <div
                  key={index}
                  className="bg-[#efede6] rounded-xl border border-[#202020]/10 overflow-hidden transition-all duration-300 hover:shadow-md"
                >
                  <button
                    onClick={() => togglePrinciple(index)}
                    className="w-full flex items-center justify-between p-4 text-left group"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <span className="text-2xl font-light text-[#202020]/40 flex-shrink-0 w-12">
                        {principle.number}
                      </span>
                      <span className="font-medium text-[#202020] group-hover:text-[#202020]/70 transition-colors">
                        {principle.title}
                      </span>
                    </div>
                    {expandedPrinciple === index ? (
                      <ChevronUp className="w-5 h-5 text-[#202020]/40 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#202020]/40 flex-shrink-0" />
                    )}
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      expandedPrinciple === index
                        ? 'max-h-[500px] pb-4'
                        : 'max-h-0'
                    }`}
                  >
                    <p className="text-[#202020]/60 leading-relaxed px-4">
                      {principle.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-[#efede6] p-4 border-t border-[#202020]/10 text-center">
            <p className="text-sm text-[#202020]/60">
              These principles form the foundation of the Contemplate program
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default PrinciplesFloatingButton;

